
import Vue from 'vue';

import SimpleFiltersCard from '@/components/filters/SimpleFiltersCard.vue';
import SelectionFilter from '@/components/filters/SelectionFilter.vue';
import AggregationIntervalFilter from '@/components/filters/AggregationIntervalFilter.vue';

import TimeRangeFilter from '@/components/filters/TimeRangeFilter.vue';
import TagFilter from '@/components/filters/TagFilter.vue';
import DeviceFilter from '@/components/filters/DeviceFilter.vue';
import ConnectorFilter from '@/components/filters/ConnectorFilter.vue';
import PropertyFilter from '@/components/filters/PropertyFilter.vue';

import {
  ReadingAggregationFrequencies,
  ReadingAggregationFunctions,
  ReadingFlagTypes,
} from '@/components/entities/reading/ReadingCollections';

import TagService from '@/services/TagService';

type EntityKey = 'reading' | 'device' | 'property' | 'connector';

type FilterGroupUiState = {
  menuOpen: Record<EntityKey, boolean>;
};

type AggregationFormState = {
  aggregationInterval: number;
  aggregationFrequency: string;
  aggregationFunction: string;
  applyFactor: boolean;
  fillMissing: boolean;
  groupByAttributeTags: boolean;
};

export default Vue.extend({
  name: 'ReadingGroupAggregationForm',

  components: {
    SimpleFiltersCard,
    SelectionFilter,
    AggregationIntervalFilter,
  },

  props: {
    value: { required: true },
  },

  data() {
    return {
      syncingFromParent: false,
      tags: [] as any[],

      entityKeys: ['reading', 'device', 'property', 'connector'] as EntityKey[],

      timeFrequencies: ReadingAggregationFrequencies,
      timeFunctions: ReadingAggregationFunctions,

      aggregationFormState: {
        aggregationInterval: 1,
        aggregationFrequency: 'DAILY',
        aggregationFunction: 'LAST',
        applyFactor: true,
        fillMissing: false,
        groupByAttributeTags: false,
      } as AggregationFormState,

      filterGroups: [] as any[],
      filterGroupUiState: [] as FilterGroupUiState[],

      readingFiltersConfig: [
        { attribute: 'timestamp', always: true },
        { attribute: 'createdAt', always: true },
        { attribute: 'flag', always: true },
      ],

      entityFiltersConfig: [
        { attribute: 'id', always: true },
        { attribute: 'ids', always: true },
        { attribute: 'tagIds', always: true },
        { attribute: 'createdAt', always: true },
      ],

      readingExtraComponents: {} as any,
      deviceExtraComponents: {} as any,
      propertyExtraComponents: {} as any,
      connectorExtraComponents: {} as any,

      pluralKeyByEntity: {
        reading: 'readings',
        device: 'devices',
        property: 'properties',
        connector: 'connectors',
      } as Record<EntityKey, string>,
    };
  },

  created() {
    this.buildExtraComponents();
    this.initFromValue(this.value);

    if (this.filterGroups.length === 0) {
      this.addFilterGroup();
    }

    this.emitBackendConfig();
    this.loadTags();
  },

  computed: {
    currentTenant(): string {
      return (this as any).$root.$store.state.session.selectedTenant.uuid;
    },

    backendConfig(): any {
      return {
        interval: Number(this.aggregationFormState.aggregationInterval),
        frequency: this.aggregationFormState.aggregationFrequency,
        function: this.aggregationFormState.aggregationFunction,
        applyFactor: !!this.aggregationFormState.applyFactor,
        fillMissing: !!this.aggregationFormState.fillMissing,
        groupByAttributeTags: !!this.aggregationFormState.groupByAttributeTags,
        filters: (this as any).buildFiltersForBackend(),
      };
    },
  },

  methods: {
    filterGroupLabel(filterGroupIndex: number) {
      const key = 'view.filterGroup';
      const translated = (this as any).$t(key, {
        index: filterGroupIndex + 1,
      }) as string;
      return translated && translated !== key
        ? translated
        : `Group ${filterGroupIndex + 1}`;
    },

    entityLabel(entityKey: EntityKey) {
      const key = `entity.${entityKey}`;
      const translated = (this as any).$t(key) as string;
      return translated && translated !== key
        ? translated
        : entityKey.charAt(0).toUpperCase() + entityKey.slice(1);
    },

    entityFilterConfig(entityKey: EntityKey) {
      return entityKey === 'reading'
        ? this.readingFiltersConfig
        : this.entityFiltersConfig;
    },

    entityExtraComponents(entityKey: EntityKey) {
      if (entityKey === 'reading') return this.readingExtraComponents;
      if (entityKey === 'device') return this.deviceExtraComponents;
      if (entityKey === 'property') return this.propertyExtraComponents;
      return this.connectorExtraComponents;
    },

    emptyFilterGroup() {
      return { reading: {}, device: {}, property: {}, connector: {} };
    },

    emptyMenuState(): Record<EntityKey, boolean> {
      return {
        reading: false,
        device: false,
        property: false,
        connector: false,
      };
    },

    emptyFilterGroupUiState(): FilterGroupUiState {
      return { menuOpen: this.emptyMenuState() };
    },

    addFilterGroup() {
      this.filterGroups.push(this.emptyFilterGroup());
      this.filterGroupUiState.push(this.emptyFilterGroupUiState());
    },

    removeFilterGroup(filterGroupIndex: number) {
      this.filterGroups.splice(filterGroupIndex, 1);
      this.filterGroupUiState.splice(filterGroupIndex, 1);

      if (this.filterGroups.length === 0) this.addFilterGroup();
    },

    buildExtraComponents() {
      this.readingExtraComponents = {
        timestamp: {
          component: TimeRangeFilter,
          attributes: { from: 'from', until: 'until' },
          label: (this as any).$t('attribute.timestamp'),
        },
        createdAt: {
          component: TimeRangeFilter,
          attributes: { from: 'createdFrom', until: 'createdUntil' },
          label: (this as any).$t('attribute.createdAt'),
        },
        flag: {
          component: SelectionFilter,
          attributes: { value: 'flag' },
          props: { items: ReadingFlagTypes },
          label: (this as any).$t('attribute.flag'),
        },
      };

      const createEntityExtraComponents = (
        entityKey: EntityKey,
        entityComponent: any
      ) => {
        const singleLabel = (this as any).$t(`entity.${entityKey}`) as string;
        const pluralLabel = (this as any).$t(
          `entity.${this.pluralKeyByEntity[entityKey]}`
        ) as string;

        return {
          id: {
            component: entityComponent,
            attributes: { value: 'id' },
            props: { multi: false },
            label: singleLabel,
          },
          ids: {
            component: entityComponent,
            attributes: { value: 'ids' },
            props: { multi: true },
            label: pluralLabel,
          },
          tagIds: {
            component: TagFilter,
            attributes: { value: 'tagIds' },
            props: { multi: true },
            label: `${singleLabel} ${
              (this as any).$t('entity.tags') as string
            }`,
          },
          createdAt: {
            component: TimeRangeFilter,
            attributes: { from: 'createdFrom', until: 'createdUntil' },
            label: (this as any).$t('attribute.createdAt'),
          },
        };
      };

      this.deviceExtraComponents = createEntityExtraComponents(
        'device',
        DeviceFilter
      );
      this.propertyExtraComponents = createEntityExtraComponents(
        'property',
        PropertyFilter
      );
      this.connectorExtraComponents = createEntityExtraComponents(
        'connector',
        ConnectorFilter
      );
    },

    initFromValue(incomingValue: any) {
      this.syncingFromParent = true;

      try {
        if (!incomingValue || typeof incomingValue !== 'object') return;

        if (incomingValue.interval !== undefined) {
          this.aggregationFormState.aggregationInterval = Number(
            incomingValue.interval
          );
        }
        if (incomingValue.frequency !== undefined) {
          this.aggregationFormState.aggregationFrequency = String(
            incomingValue.frequency
          ).toUpperCase();
        }
        if (incomingValue.function !== undefined) {
          this.aggregationFormState.aggregationFunction = String(
            incomingValue.function
          ).toUpperCase();
        }
        if (incomingValue.applyFactor !== undefined) {
          this.aggregationFormState.applyFactor = !!incomingValue.applyFactor;
        }
        if (incomingValue.fillMissing !== undefined) {
          this.aggregationFormState.fillMissing = !!incomingValue.fillMissing;
        }
        if (incomingValue.groupByAttributeTags !== undefined) {
          this.aggregationFormState.groupByAttributeTags =
            !!incomingValue.groupByAttributeTags;
        }

        if (Array.isArray(incomingValue.filters)) {
          this.filterGroups = incomingValue.filters.map((filterGroup: any) => ({
            reading: filterGroup?.reading ?? {},
            device: filterGroup?.device ?? {},
            property: filterGroup?.property ?? {},
            connector: filterGroup?.connector ?? {},
          }));

          this.filterGroupUiState = this.filterGroups.map(() =>
            this.emptyFilterGroupUiState()
          );
        }
      } finally {
        this.$nextTick(() => (this.syncingFromParent = false));
      }
    },

    removeEmptyKeys(targetObject: any) {
      if (!targetObject || typeof targetObject !== 'object') return;

      Object.keys(targetObject).forEach((propertyKey) => {
        const propertyValue = targetObject[propertyKey];

        if (propertyValue === null || propertyValue === undefined) {
          delete targetObject[propertyKey];
          return;
        }

        if (typeof propertyValue === 'string' && propertyValue.trim() === '') {
          delete targetObject[propertyKey];
          return;
        }

        if (Array.isArray(propertyValue) && propertyValue.length === 0) {
          delete targetObject[propertyKey];
          return;
        }

        if (
          propertyValue &&
          typeof propertyValue === 'object' &&
          !Array.isArray(propertyValue) &&
          Object.keys(propertyValue).length === 0
        ) {
          delete targetObject[propertyKey];
        }
      });
    },

    buildFiltersForBackend() {
      return this.filterGroups.map((filterGroup: any) => {
        const payload: any = {
          reading: { ...(filterGroup.reading || {}) },
          device: { ...(filterGroup.device || {}) },
          property: { ...(filterGroup.property || {}) },
          connector: { ...(filterGroup.connector || {}) },
        };

        this.removeEmptyKeys(payload.reading);
        this.removeEmptyKeys(payload.device);
        this.removeEmptyKeys(payload.property);
        this.removeEmptyKeys(payload.connector);

        if (payload.device && Object.keys(payload.device).length === 0)
          delete payload.device;
        if (payload.property && Object.keys(payload.property).length === 0)
          delete payload.property;
        if (payload.connector && Object.keys(payload.connector).length === 0)
          delete payload.connector;

        return payload;
      });
    },

    getEntityFilterCount(filterObj: any): number {
      if (!filterObj || typeof filterObj !== 'object') return 0;
      return Object.entries(filterObj).filter(([, v]) => {
        if (v === undefined || v === null) return false;
        if (typeof v === 'string' && v.trim() === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
      }).length;
    },

    escapeHtml(raw: any): string {
      const text = raw === null || raw === undefined ? '' : String(raw);
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },

    resolveTagName(uuid: string): string {
      const tags = (this as any).tags || [];
      const found = tags.find(
        (tagItem: any) => tagItem && tagItem.uuid === uuid
      );
      return found?.name || uuid;
    },

    formatTagIdsAsNames(tagIdsValue: any): string {
      const rawArray = Array.isArray(tagIdsValue)
        ? tagIdsValue
        : tagIdsValue
        ? [tagIdsValue]
        : [];

      return rawArray
        .map((uuid: any) =>
          typeof uuid === 'string' ? this.resolveTagName(uuid) : ''
        )
        .filter(Boolean)
        .join(', ');
    },

    formatValueForSummary(value: any): string {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean')
        return String(value);

      if (Array.isArray(value)) {
        return value
          .map((v: any) => this.formatValueForSummary(v))
          .filter(Boolean)
          .join(', ');
      }

      if (typeof value === 'object') {
        if ('uuid' in value && typeof value.uuid === 'string')
          return value.uuid;
        if ('id' in value) return String(value.id);

        return Object.values(value)
          .map((v: any) => this.formatValueForSummary(v))
          .filter(Boolean)
          .join(', ');
      }

      return String(value);
    },

    summarizeFilterBlock(blockLabel: string, blockObject: any): string {
      if (!blockObject || typeof blockObject !== 'object') return '';

      const summaryEntries = Object.entries(blockObject)
        .filter(([, entryValue]) => {
          if (entryValue === undefined || entryValue === null) return false;
          if (typeof entryValue === 'string' && (entryValue as string).trim() === '')
            return false;
          if (Array.isArray(entryValue) && entryValue.length === 0) return false;
          return true;
        })
        .map(([entryKey, entryValue]) => {
          if (entryKey === 'tagIds') {
            const tagNames = this.formatTagIdsAsNames(entryValue);
            const safeTagNames = this.escapeHtml(tagNames);
            const tagLabel = this.escapeHtml(
              (this as any).$t('entity.tags') as string
            );
            return safeTagNames ? `${tagLabel}: ${safeTagNames}` : '';
          }

          const safeKey = this.escapeHtml(entryKey);
          const safeValue = this.escapeHtml(
            this.formatValueForSummary(entryValue)
          );
          return safeValue ? `${safeKey}: ${safeValue}` : '';
        })
        .filter(Boolean);

      return summaryEntries.length
        ? `<span class="filter-summary-label">${this.escapeHtml(
            blockLabel
          )}</span> (${summaryEntries.join(' \u00b7 ')})`
        : '';
    },

    getFilterGroupSummary(filterGroupIndex: number): string[] {
      const filterGroup = this.filterGroups[filterGroupIndex];
      if (!filterGroup) return [];

      const summaryLines: string[] = [];

      for (const entityKey of this.entityKeys) {
        const label = this.entityLabel(entityKey as EntityKey);
        const line = this.summarizeFilterBlock(label, filterGroup[entityKey]);
        if (line) summaryLines.push(line);
      }

      return summaryLines;
    },

    loadTags() {
      TagService.getAllCollectionPages(this.currentTenant).then((tags: any[]) => {
        this.tags = tags;
      }).catch((err: any) => {
        console.error('Failed to load tags', err);
      });
    },

    emitBackendConfig() {
      this.$emit('input', (this as any).backendConfig);
    },
  },

  watch: {
    value: {
      deep: true,
      handler(this: any, newIncomingValue: any) {
        this.initFromValue(newIncomingValue);
      },
    },

    aggregationFormState: {
      deep: true,
      handler(this: any) {
        if (this.syncingFromParent) return;
        this.emitBackendConfig();
      },
    },

    filterGroups: {
      deep: true,
      handler(this: any) {
        if (this.syncingFromParent) return;
        this.emitBackendConfig();
      },
    },
  },
});
